<template>
    <page>
        <div class="d-flex pt-2 pb-1 paygroup-run-header">
            <div class="mr-4">
                <h5 class="text-muted">PAY GROUP</h5>
                <h4>{{ payrollRun.paygroupName }}</h4>
            </div>
            <div class="mr-4">
                <h5 class="text-muted">CHECK DATE</h5>
                <h4>{{ $filters.date(payrollRun.checkDate) }}</h4>
            </div>
            <div class="mr-4">
                <h5 class="text-muted">PAY PERIOD</h5>
                <h4>{{ $filters.date(payrollRun.payPeriodStartDate) }} - {{ $filters.date(payrollRun.payPeriodEndDate) }}</h4>
            </div>
            <payroll-steps class="ml-auto align-self-center" current="summary" />
        </div>
        <info-widget v-if="false" :collapsible="true" :is-expanded="true" class="mt-2">
            <template v-slot:title>Current Payroll Changes</template>
            <slot>
                <div class="p-2"></div>
            </slot>
        </info-widget>
        <info-widget v-if="false" :collapsible="true" :is-expanded="true" class="mt-2">
            <template v-slot:title>Credits</template>
            <slot>
                <div class="p-2"></div>
            </slot>
        </info-widget>
        <info-widget v-if="false" :collapsible="true" :is-expanded="true" class="mt-2">
            <template v-slot:title>Payroll Delivery</template>
            <slot>
                <div class="p-2"></div>
            </slot>
        </info-widget>
        <changes-report
            :changes-report="changesReport"
            :client="client"
            class="mt-2"
        />
        <div v-if="!isRunComplete && previewReportsStatus !== 'computing'" class="d-flex justify-content-end align-items-center mt-2 text-muted small">
            <span v-if="previewReportsStartedAt">Calculated {{ $filters.datetime(previewReportsStartedAt) }}</span>
            <a href class="ml-2" @click.prevent="refreshPreviewReports(true)">Recalculate</a>
        </div>
        <cash-requirements
            :cash-requirements="cashRequirements"
            :status="previewReportsStatus"
            :check-date="payrollRun.checkDate"
            class="mt-2"
        />
        <labor-allocation
            :labor-allocation="laborAllocation"
            :status="previewReportsStatus"
            class="mt-2"
        />
        <!-- <info-widget :collapsible="true" :is-expanded="true" class="mt-2">
            <template v-slot:title>Additional Reports</template>
            <slot>
                <div class="p-2"></div>
            </slot>
        </info-widget> -->

        <div v-if="previewReportsReady" class="grand-total d-flex justify-content-between pt-2 pb-2 pl-2 pr-2 mt-3">
            <h3 class="mb-0">Amount to Withdraw</h3>
            <h3 class="mb-0">${{ electronicWithdrawalsTotal }}</h3>
        </div>

        <div v-if="previewReportsReady" class="mt-3">
            <h3>Acceptance</h3>
            <p>
                By clicking "Approve and Authorize Funding," you agree that all employee pay information is correct. You agree that the total sum of <strong>${{ electronicWithdrawalsTotal }}</strong> will be withdrawn electronically from your bank accounts via EFT on <strong>{{ $filters.datelong(payrollRun.checkDate) }}</strong>. This action cannot be canceled once it has begun.
            </p>
        </div>

        <button-row inline>
            <button type="button" class="btn btn-outline-primary" @click.prevent="goToPaygrid">Back</button>
            <div>
            <button type="button" class="btn btn-outline-primary" :disabled="isRunComplete" @click.prevent="showSubmitLocationsModal = true">Submit for Approval</button>
            <button type="button" class="btn btn-primary" :disabled="isRunComplete || !previewReportsReady" @click.prevent="showApproveLocationsModal = true">Approve and Authorize Funding</button>
            </div>
        </button-row>

        <submit-locations-modal
            v-if="showSubmitLocationsModal"
            :client="client"
            :payroll-run="payrollRun"
            @close="showSubmitLocationsModal = false"
            @submitted="onSubmitted"
        />

        <approve-locations-modal
            v-if="showApproveLocationsModal"
            :client="client"
            :payroll-run="payrollRun"
            @close="showApproveLocationsModal = false"
            @approved="onApproved"
            @approving-payroll="onApprovingPayroll"
        />
    </page>
</template>

<script>
import Page from '@/components/Page'
import ButtonRow from '@/components/ButtonRow'
import InfoWidget from '@/components/InfoWidget'
import SubmitLocationsModal from './SubmitLocationsModal'
import ApproveLocationsModal from './ApproveLocationsModal'
import LaborAllocation from './LaborAllocation'
import CashRequirements from './CashRequirements'
import ChangesReport from './ChangesReport'
import PayrollSteps from './components/PayrollSteps'

export default {
    components: {Page, ButtonRow, InfoWidget, SubmitLocationsModal, ApproveLocationsModal, LaborAllocation, CashRequirements, ChangesReport, PayrollSteps},
    props: ['client'],
    data() {
        return {
            dataLoaded: false,
            showSubmitLocationsModal: false,
            showApproveLocationsModal: false,
            payrollRun: {
                id: this.$route.params.runId,
                paygroupName: '',
                // alerts: [],
                checkDate: null,
                payPeriodStartDate: null,
                payPeriodEndDate: null,
                lastSavedTime: '',
                companies: [],
                workLocations: [],
                submittedLocationIds: [],
                approvedLocationIds: [],
                status: null,
            },
            // Labor Allocation and Cash Requirements, calculated in the background
            previewReports: null,
            previewReportsStatus: null,
            previewReportsStartedAt: null,
            changesReport: null,
            completingTaskId: null,
        }
    },
    computed: {
        // Approved runs complete immediately, so both statuses mean the run is done
        isRunComplete() {
            return ['approved', 'completed'].includes(this.payrollRun.status)
        },
        // The Acceptance text and the Approve button wait for this, so neither shows until the
        // funding total is known. A failed calculation keeps approval blocked: the same problem
        // would likely stop completing the payroll too.
        previewReportsReady() {
            return this.previewReportsStatus === 'ready'
        },
        cashRequirements() {
            return this.previewReports ? this.previewReports.cash_requirements : null
        },
        laborAllocation() {
            return this.previewReports ? this.previewReports.labor_allocation : null
        },
        // "Amount to Withdraw": the total of the Electronic Withdrawals section: direct-deposit
        // net pay, third-party payments and all payroll taxes (employee + employer), funded by EFT.
        electronicWithdrawalsTotal() {
            const accounts = (this.cashRequirements && this.cashRequirements.electronic_withdrawals) || []
            const sum = accounts.reduce((acc, account) => acc + (parseFloat(account.amount) || 0), 0)
            return sum.toFixed(2)
        },
    },
    mounted() {
        this.$store.dispatch('SET_PAGE_TITLE', 'Payroll')
        // Load the run, then make sure its reports are current. In that order, so loading the run
        // can't overwrite a calculation that has just started.
        this.getData().then(() => {
            this.refreshPreviewReports(false)
        })
    },
    unmounted() {
        this._stopPolling()
        this._stopCompletionPolling()
    },
    methods: {
        getData(silent) {
            if (!silent) {
                this.dataLoaded = false
                this.$store.dispatch('START_LOADING')
            }
            this.refreshing = true

            let url
            const paygroupId = this.$route.params.paygroupId
            const collectionId = this.$route.params.collectionId
            const payPeriodStart = this.$route.query.pay_period_start_date
            if (collectionId) {
                url = `/clients/${this.client.id}/payroll/collections/${collectionId}/summary?pay_period_start_date=${payPeriodStart}`
            } else if (paygroupId && this.payrollRun.id) {
                url = `/clients/${this.client.id}/payroll/runs/${this.payrollRun.id}/summary`
            }

            // let resp = paygridData

            return this.$api.get(url).then(resp => {
                this.payrollRun.companies = resp['companies']
                this.payrollRun.workLocations = resp['work_locations'] || []
                this.payrollRun.submittedLocationIds = resp['submitted_location_ids'] || []
                this.payrollRun.approvedLocationIds = resp['approved_location_ids'] || []
                this.payrollRun.paygroupName = resp['paygroup_name']
                this.payrollRun.payPeriodStartDate = resp['pay_period_start_date']
                this.payrollRun.payPeriodEndDate = resp['pay_period_end_date']
                this.payrollRun.checkDate = resp['check_date']
                this.payrollRun.lastSavedTime = resp['last_saved_time']
                this.payrollRun.status = resp['status']
                this.setPreviewReports(resp)
                this.changesReport = resp['changes_report'] || null

                this.dataLoaded = true
                this.refreshing = false
                if (!silent) {
                    this.$store.dispatch('STOP_LOADING')
                }
            }).catch((errors) => {
                if (errors.__status__ == 403) {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.$emit('no-access')
                    return
                }
                if (!silent) {
                    this.$store.dispatch('STOP_LOADING')
                }
                this.refreshing = false
                this.$bus.showError(errors.__all__)
            })
        },
        setPreviewReports(resp) {
            this.previewReports = resp['preview_reports']
            this.previewReportsStatus = resp['preview_reports_status']
            this.previewReportsStartedAt = resp['preview_reports_started_at']
            if (this.previewReportsStatus === 'computing') {
                this._startPolling()
            } else {
                this._stopPolling()
            }
        },
        // Recalculates only if the paygrid was saved since the last calculation, unless forced
        // (for changes made elsewhere, like an employee's deductions). Skipped for collection
        // summaries, since the endpoint is per run. A failure here leaves the rest of the page
        // working.
        refreshPreviewReports(force) {
            if (this.$route.params.collectionId || !this.payrollRun.id) {
                return
            }
            const url = `/clients/${this.client.id}/payroll/runs/${this.payrollRun.id}/preview-reports/refresh`
            this.$api.post(url, {force: force}).then(resp => {
                this.setPreviewReports(resp)
            }).catch(() => {})
        },
        _startPolling() {
            if (this._previewReportsPoller) return
            const url = `/clients/${this.client.id}/payroll/runs/${this.payrollRun.id}/preview-reports`
            this._previewReportsPoller = setInterval(() => {
                this.$api.get(url).then(resp => {
                    this.setPreviewReports(resp)
                }).catch(() => {
                    this._stopPolling()
                })
            }, 3000)
        },
        _stopPolling() {
            if (this._previewReportsPoller) {
                clearInterval(this._previewReportsPoller)
                this._previewReportsPoller = null
            }
        },
        goToPaygrid() {
            // TODO KB PG logic for collection summary screen
            // Back goes to the last paygrid step, salaried
            this.$router.push({
                path: `/clients/${this.$route.params.hashedClientId}/payroll/paygrid/paygroup/${this.$route.params.paygroupId}/run/${this.$route.params.runId}/salaried`
            })
        },
        goToStartScreen() {
            this.$router.push({
                path: `/clients/${this.$route.params.hashedClientId}/payroll/`,
            })
        },
        onSubmitted() {
            this.goToStartScreen()
        },
        onApproved() {
            this.getData(true)
        },
        onApprovingPayroll(taskId) {
            this.completingTaskId = taskId
            this.$store.dispatch('START_LOADING')
            this._startCompletionPolling()
        },
        _startCompletionPolling() {
            if (this._completionPoller) return
            this._completionPoller = setInterval(() => {
                const url = `/clients/${this.client.id}/payroll/runs/${this.payrollRun.id}/complete/progress?task_id=${this.completingTaskId}`
                this.$api.get(url).then(resp => {
                    if (resp.status === 'SUCCESS') {
                        this._stopCompletionPolling()
                        this.$store.dispatch('STOP_LOADING')
                        this.$bus.showInfo('Successfully completed payroll.')
                        this.goToStartScreen()
                    } else if (resp.status === 'FAILURE') {
                        this._stopCompletionPolling()
                        this.$store.dispatch('STOP_LOADING')
                        this.$bus.showError(resp.error || 'Payroll completion failed.')
                    }
                }).catch((errors) => {
                    this._stopCompletionPolling()
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showError(errors.__all__)
                })
            }, 2000)
        },
        _stopCompletionPolling() {
            if (this._completionPoller) {
                clearInterval(this._completionPoller)
                this._completionPoller = null
            }
        },
    },
}
</script>

<style lang="scss" scoped>
    .paygroup-run-header {
        h5 {
            font-weight: 500;
        }
        border-bottom: 1px solid #ddd;
    }
    .table-indent-1 {
        padding-left: 2rem !important;
    }
    .vertical-align-middle {
        vertical-align: middle;
    }
    .tr-height {
        height: 32px;
    }
    .grand-total {
        background-color: #F7F7F7;
        border: 2px solid #ccc;
    }
</style>
